import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { warmProjectChunk } from "./lib/project-chunk"
import { startInstallCapture } from "./lib/pwa"
import { startBootRequests } from "./lib/queries"
import { createQueryClient } from "./lib/query-client"
import { handleForbidden, handleUnauthorized } from "./lib/session"
import { markBoot, startErrorReporting, startVitals } from "./lib/telemetry"
import { startWebMcp } from "./lib/webmcp/webmcp"
import { startProjectBoot } from "./model/live/warm"
import { createAppRouter } from "./router"
import "./styles.css"

markBoot("main")

// Continuous deploys: when a new service worker takes control (skipWaiting + clientsClaim), reload once so
// the page runs the new bundle instead of the one the previous worker served. Skipped on the very first
// install, where there was no controller before.
if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
  let reloading = false
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  })
}

// The two reference each other: a 401 anywhere sends the person to sign-in through the router.
const queryClient = createQueryClient({
  onUnauthorized: () => handleUnauthorized(queryClient, router),
  onForbidden: (notMember) => handleForbidden(queryClient, router, notMember),
})
const router = createAppRouter(queryClient)

// Decision 0113: keep the browser's install offer for "Install Minga"; route a tapped push notification in place
// (the service worker posts its link to an open window, public/push-sw.js).
startInstallCapture()
if ("serviceWorker" in navigator)
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data as { type?: unknown; url?: unknown } | null
    if (data?.type === "minga:navigate" && typeof data.url === "string" && /^\/(?!\/)/.test(data.url))
      router.history.push(data.url)
  })

// The session and the workspace list are asked for together, before the route guards wait on them one after the other.
startBootRequests(queryClient, window.location.pathname)
// Project load speed (2026-10-17): a project URL opens its socket as soon as the session is known.
startProjectBoot(queryClient, window.location)
// The project pages are a lazy route chunk: a project URL starts it now, other workspace pages when the browser is idle.
warmProjectChunk(window.location.pathname)
startVitals()
startErrorReporting()
// Decision 0123: WebMCP, Minga's tools for a browser agent acting for the signed-in person (when the browser has it).
startWebMcp({ router, queryClient })

const root = document.getElementById("root")
if (!root) throw new Error("#root missing")

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
